import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  FileUp,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  UploadCloud,
  User,
  X,
} from "lucide-react";
import { CATEGORIES } from "../data/services";
import { Eyebrow, Reveal, INPUT_CLASS } from "../lib/ui";

const MAX_FILES = 5;
const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const STEPS = ["Type", "Emne", "Sagen", "Kontakt", "Resume"];

const TYPE_OPTIONS = [
  {
    value: "privatperson",
    label: "Privatperson",
    desc: "Boligkøb, familie, testamente, skøder og dokumenter til privat brug.",
    icon: User,
  },
  {
    value: "virksomhed",
    label: "Erhverv",
    desc: "Selskabsret, ejeraftaler, direktørkontrakter og erhvervsejendomme.",
    icon: Building2,
  },
];

const AREA_LABEL = { andet: "Andet / ved ikke" };
CATEGORIES.forEach((c) => {
  AREA_LABEL[c.slug] = c.title;
  c.items.forEach((p) => (AREA_LABEL[p.slug] = p.title));
});

// Hvilken målgruppe hver kategori/ydelse passer til: "privat", "erhverv" eller "begge".
const CATEGORY_AUDIENCE = {
  boligadvokat: "privat",
  tinglysning: "begge",
  selskabsadvokat: "erhverv",
  "familie-og-arv": "privat",
};
const SERVICE_AUDIENCE = {
  "tinglysning-af-skoede": "privat",
  skilsmisseskoede: "privat",
  debitorskifte: "erhverv",
};
const audFor = (slug) => SERVICE_AUDIENCE[slug] || CATEGORY_AUDIENCE[slug];
const matchesAudience = (aud, clientType) =>
  aud === "begge" ||
  aud === (clientType === "virksomhed" ? "erhverv" : "privat");

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  client_type: "",
  category: "",
  area: "",
  message: "",
  consent: false,
  contact_method: "email",
};

const Progress = ({ step }) => (
  <div data-testid="wizard-progress" className="mb-8">
    <p className="mb-3 text-xs font-semibold text-slate-400 lg:hidden">
      Trin {step + 1} af {STEPS.length} — {STEPS[step]}
    </p>
    <div className="hidden items-center justify-between gap-1 lg:flex">
      {STEPS.map((label, i) => (
        <div key={label} className="flex flex-1 items-center gap-1.5">
          <div className="flex items-center gap-2">
            <span
              data-testid={`wizard-step-${i}`}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors duration-200 ${
                i < step
                  ? "bg-brand-600 text-white"
                  : i === step
                    ? "bg-brand-950 text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
            >
              {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span
              className={`hidden text-xs font-medium lg:block ${i <= step ? "text-slate-900" : "text-slate-400"}`}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px flex-1 ${i < step ? "bg-brand-600" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  </div>
);

const StepType = ({ value, onSelect }) => (
  <div>
    <h2 className="text-lg font-semibold text-slate-900">Hvem henvender du dig som?</h2>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
      Så sørger vi for, at din henvendelse lander hos den rette rådgiver.
    </p>
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {TYPE_OPTIONS.map((t) => {
        const Icon = t.icon;
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`contact-type-${t.value}`}
            onClick={() => onSelect(t.value)}
            className={`rounded-2xl border p-5 text-left transition-all duration-200 ${
              active
                ? "border-brand-950 bg-brand-50/60 shadow-sm"
                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-200 ${
                active ? "bg-brand-950 text-white" : "bg-slate-100 text-slate-500"
              }`}
            >
              <Icon className="h-5 w-5" strokeWidth={1.6} />
            </span>
            <span className="mt-3 block text-base font-semibold text-slate-900">{t.label}</span>
            <span className="mt-1 block text-sm leading-relaxed text-slate-500">{t.desc}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const chipClass = (active) =>
  `rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
    active
      ? "border-brand-950 bg-brand-950 text-white shadow-sm"
      : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
  }`;

const StepArea = ({ form, onSelectCategory, onSelectService, onSelectAndet }) => {
  const categories = CATEGORIES.filter((c) =>
    matchesAudience(CATEGORY_AUDIENCE[c.slug], form.client_type)
  );
  const category = categories.find((c) => c.slug === form.category);
  const items = category
    ? category.items.filter((p) =>
        matchesAudience(SERVICE_AUDIENCE[p.slug] || CATEGORY_AUDIENCE[category.slug], form.client_type)
      )
    : [];
  const andetAktiv = form.area === "andet";
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900">Hvad drejer sagen sig om?</h2>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
        Vælg et hovedområde til venstre — så finder du den konkrete ydelse ved siden af.
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div data-testid="area-column-main">
          <p className="mb-2 text-xs font-semibold text-slate-400">Hovedområde</p>
          <div className="grid gap-2">
            {categories.map((c) => {
              const active = form.category === c.slug && !andetAktiv;
              return (
                <button
                  key={c.slug}
                  type="button"
                  data-testid={`contact-area-${c.slug}`}
                  onClick={() => onSelectCategory(c.slug)}
                  className={`flex w-full items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all duration-200 ${
                    active
                      ? "border-brand-950 bg-brand-950 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {c.title}
                  <ChevronRight
                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-300"}`}
                  />
                </button>
              );
            })}
            <button
              type="button"
              data-testid="contact-area-andet"
              onClick={onSelectAndet}
              className={`flex w-full items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all duration-200 ${
                andetAktiv
                  ? "border-brand-950 bg-brand-50/60 text-brand-950"
                  : "border-dashed border-slate-300 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              Andet / ved ikke
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          </div>
        </div>

        <div data-testid="contact-service-picker">
          <p className="mb-2 text-xs font-semibold text-slate-400">Konkret ydelse</p>
          {andetAktiv ? (
            <div className="flex min-h-[180px] items-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-4 text-sm leading-relaxed text-slate-400">
              Du har valgt "Andet / ved ikke" — så matcher vi dig med den rette rådgiver.
            </div>
          ) : category ? (
            <div className="min-h-[180px] rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="contact-service-whole"
                  onClick={() => onSelectService(category.slug)}
                  className={chipClass(form.area === category.slug)}
                >
                  Hele området
                </button>
                {items.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    data-testid={`contact-service-${p.slug}`}
                    onClick={() => onSelectService(p.slug)}
                    className={chipClass(form.area === p.slug)}
                  >
                    {p.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex min-h-[180px] items-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-4 text-sm leading-relaxed text-slate-400">
              Vælg først et hovedområde — så viser vi de konkrete ydelser her.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StepCase = ({ form, files, onChange, addFiles, removeFile, fileInputRef }) => (
  <div>
    <h2 className="text-lg font-semibold text-slate-900">Fortæl kort om din sag</h2>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
      Hvad er der sket, og hvad vil du gerne have hjælp til?
    </p>
    <textarea
      id="cf-message"
      data-testid="contact-input-message"
      rows={5}
      className={`${INPUT_CLASS} mt-5 resize-y`}
      placeholder="Beskriv kort din situation — hvad er der sket, og hvad vil du gerne have hjælp til?"
      value={form.message}
      onChange={onChange("message")}
    />
    <div className="mt-5">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">
        Upload dokumenter <span className="font-normal text-slate-400">(valgfrit)</span>
      </span>
      <button
        type="button"
        data-testid="contact-input-files"
        onClick={() => fileInputRef.current?.click()}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 px-4 py-6 text-center transition-colors duration-200 hover:border-brand-600/50 hover:bg-brand-50/40"
      >
        <UploadCloud className="h-6 w-6 text-brand-600" strokeWidth={1.6} />
        <span className="text-sm font-medium text-slate-700">
          Træk filer hertil, eller klik for at vælge
        </span>
        <span className="text-xs text-slate-400">
          PDF, DOC, DOCX, JPG eller PNG — højst 5 filer à 5 MB
        </span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={addFiles}
        aria-label="Vedhæft dokumenter"
      />
      {files.length > 0 && (
        <ul data-testid="contact-file-list" className="mt-3 space-y-2">
          {files.map((f) => (
            <li
              key={f.name}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-slate-700">
                <FileUp className="h-4 w-4 shrink-0 text-brand-600" />
                <span className="truncate">{f.name}</span>
              </span>
              <button
                type="button"
                data-testid={`contact-file-remove-${f.name}`}
                onClick={() => removeFile(f.name)}
                aria-label={`Fjern ${f.name}`}
                className="ml-3 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const StepContact = ({ form, onChange }) => (
  <div>
    <h2 className="text-lg font-semibold text-slate-900">Hvordan kontakter vi dig?</h2>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
      Vi bruger kun dine oplysninger til at vende tilbage om din sag.
    </p>
    <div className="mt-5 grid gap-5">
      <div>
        <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-slate-700">
          Navn
        </label>
        <input
          id="cf-name"
          data-testid="contact-input-name"
          className={INPUT_CLASS}
          placeholder="Dit fulde navn"
          value={form.name}
          onChange={onChange("name")}
        />
      </div>
      <div>
        <label htmlFor="cf-phone" className="mb-1.5 block text-sm font-medium text-slate-700">
          Telefonnummer
        </label>
        <input
          id="cf-phone"
          data-testid="contact-input-phone"
          type="tel"
          className={INPUT_CLASS}
          placeholder="F.eks. 20 12 34 56"
          value={form.phone}
          onChange={onChange("phone")}
        />
      </div>
      <div>
        <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-slate-700">
          E-mail
        </label>
        <input
          id="cf-email"
          data-testid="contact-input-email"
          type="email"
          className={INPUT_CLASS}
          placeholder="din@mail.dk"
          value={form.email}
          onChange={onChange("email")}
        />
        <p className="mt-1.5 text-xs text-slate-400">
          Du får automatisk en kopi af din henvendelse på denne adresse.
        </p>
      </div>
    </div>
  </div>
);

const SummaryRow = ({ label, value, testid }) => (
  <div className="flex gap-4 px-4 py-3 sm:px-5">
    <dt className="w-24 shrink-0 text-sm text-slate-500 sm:w-32">{label}</dt>
    <dd data-testid={testid} className="min-w-0 flex-1 break-words text-sm font-medium text-slate-900">
      {value}
    </dd>
  </div>
);

const METHOD_OPTIONS = [
  { value: "email", label: "Svar på e-mail", icon: Mail, desc: "Vi svarer på din e-mail, når vi har gennemgået sagen." },
  { value: "opkald", label: "Ring mig op", icon: Phone, desc: "Vi ringer til dig på det angivne telefonnummer." },
];

const StepSummary = ({ form, files, onConsent, onMethod }) => (
  <div>
    <h2 className="text-lg font-semibold text-slate-900">Gennemgå din henvendelse</h2>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
      Tjek lige, at alt passer, inden du sender.
    </p>
    <dl
      data-testid="contact-summary"
      className="mt-5 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white"
    >
      <SummaryRow
        label="Type"
        testid="summary-type"
        value={TYPE_OPTIONS.find((t) => t.value === form.client_type)?.label || "—"}
      />
      <SummaryRow label="Område" testid="summary-area" value={AREA_LABEL[form.area] || form.area || "—"} />
      <SummaryRow label="Din besked" testid="summary-message" value={form.message} />
      <SummaryRow
        label="Dokumenter"
        testid="summary-files"
        value={files.length ? files.map((f) => f.name).join(", ") : "Ingen"}
      />
      <SummaryRow label="Navn" testid="summary-name" value={form.name} />
      <SummaryRow label="Telefon" testid="summary-phone" value={form.phone} />
      <SummaryRow label="E-mail" testid="summary-email" value={form.email} />
      <SummaryRow
        label="Kontaktmetode"
        testid="summary-method"
        value={METHOD_OPTIONS.find((m) => m.value === form.contact_method)?.label || "Svar på e-mail"}
      />
    </dl>
    <div className="mt-5">
      <p className="mb-2 text-sm font-medium text-slate-700">Hvordan vil du helst kontaktes?</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {METHOD_OPTIONS.map((m) => {
          const Icon = m.icon;
          const active = form.contact_method === m.value;
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`contact-method-${m.value}`}
              onClick={() => onMethod(m.value)}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all duration-200 ${
                active
                  ? "border-brand-950 bg-brand-50/60 shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ${
                  active ? "bg-brand-950 text-white" : "bg-slate-100 text-slate-500"
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">{m.label}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{m.desc}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
    <label htmlFor="cf-consent" className="mt-5 flex cursor-pointer items-start gap-3">
      <input
        id="cf-consent"
        data-testid="contact-checkbox-consent"
        type="checkbox"
        checked={form.consent}
        onChange={onConsent}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
      />
      <span className="text-sm leading-relaxed text-slate-600">
        Jeg accepterer, at mine oplysninger behandles i henhold til{" "}
        <Link
          to="/privatlivspolitik"
          data-testid="contact-privacy-link"
          className="font-medium text-brand-600 underline-offset-2 hover:underline"
        >
          privatlivspolitikken
        </Link>
        .
      </span>
    </label>
  </div>
);

const SideSummary = ({ form, files, step, maxVisited, onJump, onRemoveFile }) => {
  const typeLabel = TYPE_OPTIONS.find((t) => t.value === form.client_type)?.label;
  const message = form.message.trim();
  const messagePreview = message
    ? message.length > 44
      ? `${message.slice(0, 44).trimEnd()}…`
      : message
    : "";
  const rows = [
    { idx: 0, label: "Type", value: typeLabel || "", done: Boolean(typeLabel) },
    {
      idx: 1,
      label: "Emne",
      value: form.area ? AREA_LABEL[form.area] || form.area : "",
      done: Boolean(form.area),
    },
    {
      idx: 2,
      label: "Sagen",
      value: messagePreview,
      done: message.length >= 10,
    },
    {
      idx: 3,
      label: "Kontakt",
      value: [form.name, form.email].filter(Boolean).join(" · "),
      done: Boolean(form.name.trim() && form.email.trim()),
    },
    { idx: 4, label: "Resume", value: form.contact_method === "opkald" ? "Ring mig op" : "Gennemgå og send", done: false },
  ];

  return (
    <div data-testid="side-summary" className="rounded-2xl border border-slate-200 bg-white p-5">
      <p className="text-sm font-semibold text-slate-900">Dine valg</p>
      <ul className="mt-2 space-y-0.5">
        {rows.map((r) => {
          const current = step === r.idx;
          const reachable = r.idx <= maxVisited;
          return (
            <li key={r.idx}>
              <button
                type="button"
                data-testid={`side-summary-row-${r.idx}`}
                disabled={!reachable || current}
                onClick={() => onJump(r.idx)}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors duration-200 ${
                  current
                    ? "bg-brand-50/70"
                    : reachable
                      ? "cursor-pointer hover:bg-slate-50"
                      : "cursor-default"
                } ${!reachable && !current ? "opacity-45" : ""}`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors duration-200 ${
                    r.done || current
                      ? "bg-brand-950 text-white"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {r.done && !current ? <Check className="h-3 w-3" /> : r.idx + 1}
                </span>
                <span className="w-12 shrink-0 text-xs font-semibold text-slate-900">
                  {r.label}
                </span>
                <span
                  title={r.value || undefined}
                  className="min-w-0 flex-1 truncate text-right text-xs text-slate-500"
                >
                  {r.value || <span className="text-slate-300">Ikke udfyldt</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t border-slate-100 pt-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <FileUp className="h-4 w-4 text-brand-600" />
          Dokumenter
        </p>
        {files.length === 0 ? (
          <p className="mt-2 text-xs leading-relaxed text-slate-400">
            Ingen valgt endnu — du kan vedhæfte i trin 3.
          </p>
        ) : (
          <ul data-testid="side-file-list" className="mt-2 space-y-1.5">
            {files.map((f) => (
              <li
                key={f.name}
                className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5"
              >
                <span className="flex min-w-0 items-center gap-2 text-xs text-slate-700">
                  <span className="truncate" title={f.name}>
                    {f.name}
                  </span>
                </span>
                <button
                  type="button"
                  data-testid={`side-file-remove-${f.name}`}
                  onClick={() => onRemoveFile(f.name)}
                  aria-label={`Fjern ${f.name}`}
                  className="shrink-0 rounded-full p-1 text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-3 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-400">
        Klik på et trin for at springe tilbage og ret det hurtigt.
      </p>
    </div>
  );
};

export const KontaktPage = () => {
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [maxVisited, setMaxVisited] = useState(0);
  const fileInputRef = useRef();

  useEffect(() => {
    const areaHandler = (e) => {
      const slug = e.detail;
      setForm((f) => {
        const cat = CATEGORIES.find(
          (c) => c.slug === slug || c.items.some((p) => p.slug === slug)
        );
        if (!cat) return f;
        return { ...f, category: cat.slug, area: slug };
      });
    };
    const typeHandler = (e) =>
      setForm((f) => ({ ...f, client_type: e.detail }));
    window.addEventListener("juris:prefill-area", areaHandler);
    window.addEventListener("juris:prefill-type", typeHandler);
    return () => {
      window.removeEventListener("juris:prefill-area", areaHandler);
      window.removeEventListener("juris:prefill-type", typeHandler);
    };
  }, []);

  useEffect(() => {
    if (location.state?.area) {
      window.dispatchEvent(
        new CustomEvent("juris:prefill-area", { detail: location.state.area })
      );
    }
  }, [location.state]);

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: key === "consent" ? e.target.checked : e.target.value,
    }));

  const goTo = (n) => {
    setDir(n > step ? 1 : -1);
    setStep(n);
  };

  const validateStep = (n) => {
    if (n === 0 && !form.client_type)
      return "Vælg om du henvender dig som privatperson eller virksomhed.";
    if (n === 1 && !form.area) return "Vælg venligst, hvad sagen drejer sig om.";
    if (n === 2 && form.message.trim().length < 10)
      return "Beskriv venligst din sag kort (mindst 10 tegn).";
    if (n === 3) {
      if (form.name.trim().length < 2) return "Angiv venligst dit navn.";
      if (!/^\+?[0-9][0-9\s\-]{5,}$/.test(form.phone.trim()))
        return "Angiv venligst et gyldigt telefonnummer.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
        return "Angiv venligst en gyldig e-mail.";
    }
    if (n === 4 && !form.consent)
      return "Du skal acceptere privatlivspolitikken, før du kan sende.";
    return "";
  };

  const next = () => {
    const v = validateStep(step);
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setMaxVisited((m) => Math.max(m, step + 1));
    goTo(step + 1);
  };

  const advanceFrom = (from) => {
    if (step !== from) return;
    setError("");
    setMaxVisited((m) => Math.max(m, step + 1));
    goTo(step + 1);
  };

  const back = () => {
    setError("");
    goTo(Math.max(0, step - 1));
  };

  const addFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      const merged = [...prev];
      for (const f of incoming) {
        if (!names.has(f.name) && merged.length < MAX_FILES) merged.push(f);
      }
      return merged;
    });
    e.target.value = "";
  };

  const removeFile = (name) =>
    setFiles((prev) => prev.filter((f) => f.name !== name));

  const submit = async () => {
    const v = validateStep(4);
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("client_type", form.client_type);
      fd.append("area", form.area);
      fd.append("message", form.message);
      fd.append("consent", "true");
      fd.append("contact_method", form.contact_method);
      files.forEach((f) => fd.append("files", f));

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/inquiries`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Noget gik desværre galt. Prøv igen.");
      setSuccess({ reference: data.reference, copy_sent: data.copy_sent });
    } catch (err) {
      setError(err.message || "Noget gik desværre galt. Prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const jumpTo = (idx) => {
    setError("");
    goTo(idx);
  };

  const reset = () => {
    setSuccess(null);
    setForm(EMPTY_FORM);
    setFiles([]);
    setError("");
    setMaxVisited(0);
    goTo(0);
  };

  const selectType = (v) => {
    setForm((f) => {
      const okArea = Boolean(f.area) && matchesAudience(audFor(f.area), v);
      return {
        ...f,
        client_type: v,
        category: okArea ? f.category : "",
        area: okArea ? f.area : "",
      };
    });
    advanceFrom(0);
  };
  const selectCategory = (slug) =>
    setForm((f) => ({ ...f, category: slug, area: slug }));
  const selectService = (slug) => {
    setForm((f) => ({ ...f, area: slug }));
    advanceFrom(1);
  };
  const selectAndet = () => {
    setForm((f) => ({ ...f, category: "", area: "andet" }));
    advanceFrom(1);
  };

  const stepContent =
    step === 0 ? (
      <StepType value={form.client_type} onSelect={selectType} />
    ) : step === 1 ? (
      <StepArea
        form={form}
        onSelectCategory={selectCategory}
        onSelectService={selectService}
        onSelectAndet={selectAndet}
      />
    ) : step === 2 ? (
      <StepCase
        form={form}
        files={files}
        onChange={set}
        addFiles={addFiles}
        removeFile={removeFile}
        fileInputRef={fileInputRef}
      />
    ) : step === 3 ? (
      <StepContact form={form} onChange={set} />
    ) : (
      <StepSummary
        form={form}
        files={files}
        onConsent={set("consent")}
        onMethod={(v) => setForm((f) => ({ ...f, contact_method: v }))}
      />
    );

  const isLast = step === STEPS.length - 1;

  return (
    <main data-testid="kontakt-page" className="min-h-screen bg-slate-50/60">
      <header className="bg-brand-950 pb-14 pt-[120px] sm:pb-16 sm:pt-[140px]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow dark>Kontakt</Eyebrow>
            <h1
              data-testid="kontakt-page-heading"
              className="mt-3 max-w-2xl text-3xl font-semibold tracking-[-0.02em] text-white sm:text-5xl"
            >
              Fortæl os om din sag
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">
              I fem nemme trin — det tager under to minutter, og så kontakter vi dig med
              næste skridt. Du får altid en kopi af din henvendelse på e-mail.
            </p>
            <ul className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <li className="flex items-center gap-2.5 text-white/85">
                <Mail className="h-4 w-4 text-white/60" />
                <a
                  href="mailto:kontakt@juris.dk"
                  data-testid="contact-email-link"
                  className="font-medium hover:text-white"
                >
                  kontakt@juris.dk
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-white/85">
                <MapPin className="h-4 w-4 text-white/60" />
                Odeons Kvarter 10, 1 — 5000 Odense C
              </li>
              <li className="flex items-center gap-2.5 text-white/60">
                <Lock className="h-4 w-4 text-white/50" />
                Dine oplysninger behandles fortroligt.
              </li>
            </ul>
          </Reveal>
        </div>
      </header>

      <div className="pb-20 pt-10 sm:pb-24 sm:pt-12">
        <div className="mx-auto grid max-w-[1440px] items-start gap-6 px-6 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
          <Reveal delay={0.05} className="min-w-0 lg:col-span-8">
            <div
              className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-9"
              data-testid="contact-form-card"
            >
              {success ? (
                <div
                  data-testid="contact-success"
                  className="flex flex-col items-center py-10 text-center"
                >
                  <CheckCircle2 className="h-14 w-14 text-emerald-500" strokeWidth={1.5} />
                  <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">
                    Tak — din henvendelse er modtaget
                  </h2>
                  <p className="mt-3 max-w-sm text-base leading-relaxed text-slate-600">
                    {form.contact_method === "opkald"
                      ? `Vi ringer til dig hurtigst muligt på ${form.phone}, så vi sammen kan aftale næste skridt.`
                      : "Vi gennemgår din sag og kontakter dig hurtigst muligt, så vi sammen kan aftale næste skridt."}
                  </p>
                  {form.email &&
                    (success.copy_sent ? (
                      <p
                        data-testid="contact-success-copy"
                        className="mt-4 max-w-sm text-sm leading-relaxed text-slate-500"
                      >
                        En kopi af din henvendelse er sendt til{" "}
                        <span className="font-medium text-slate-700">{form.email}</span>.
                      </p>
                    ) : (
                      <p
                        data-testid="contact-success-copy-fallback"
                        className="mt-4 max-w-sm text-sm leading-relaxed text-slate-500"
                      >
                        Bemærk: Vi kunne desværre ikke sende en kvitteringsmail til{" "}
                        <span className="font-medium text-slate-700">{form.email}</span> lige nu —
                        din henvendelse er dog modtaget.
                      </p>
                    ))}
                  <p className="mt-6 rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
                    Sagsnummer: {success.reference}
                  </p>
                  <button
                    type="button"
                    data-testid="contact-success-new"
                    onClick={reset}
                    className="mt-8 text-sm font-semibold text-brand-600 hover:text-brand-700"
                  >
                    Send en ny henvendelse
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-wizard"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isLast) submit();
                    else next();
                  }}
                >
                  <Progress step={step} />
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 24 * dir }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 * dir }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      {stepContent}
                    </motion.div>
                  </AnimatePresence>

                  {error && (
                    <p
                      data-testid="contact-error"
                      role="alert"
                      className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    >
                      {error}
                    </p>
                  )}

                  <div className="mt-8 flex items-center justify-between gap-3">
                    {step > 0 ? (
                      <button
                        type="button"
                        data-testid="wizard-back"
                        onClick={back}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-slate-300 hover:text-slate-900"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Tilbage
                      </button>
                    ) : (
                      <span />
                    )}
                    <button
                      type="submit"
                      data-testid={isLast ? "contact-form-submit" : "wizard-next"}
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-950 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-brand-950/30 transition-all duration-200 hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sender…
                        </>
                      ) : isLast ? (
                        "Send din henvendelse"
                      ) : (
                        <>
                          Næste
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>

          {!success && (
            <Reveal
              delay={0.1}
              className="min-w-0 self-start lg:sticky lg:top-24 lg:col-span-4"
            >
              <SideSummary
                form={form}
                files={files}
                step={step}
                maxVisited={maxVisited}
                onJump={jumpTo}
                onRemoveFile={removeFile}
              />
            </Reveal>
          )}
        </div>
      </div>
    </main>
  );
};

export default KontaktPage;
